import { Lock } from '@mui/icons-material'
import {
  Avatar,
  Divider,
  DividerProps,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router'
import { Link, useSearchParams } from 'react-router-dom'
import { SupportedLocale } from 'src/@types'
import { ReactComponent as Logo } from 'src/assets/logo-gfs.svg'
import { useEnvironment } from 'src/env/EnvironmentStore'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { MenuItemNavLink } from 'src/routing/MenuItemNavLink'
import { ROUTES } from 'src/routing/routes'
import { TabNavLink } from 'src/routing/TabNavLink'
import { usePermissionsForSuccessFactors } from 'src/service/security/PermissionHook'
import { PermissionService } from 'src/service/security/PermissionService'
import { BASE_URL, PROCESS, PROJECT } from 'src/shared/constants/constants'
import { ENVIRONMENT } from 'src/shared/constants/environment-constants'
import { TYPOGRAPHY_MIXIN } from 'src/shared/constants/styling-constants'
import { AccountIcon, CheckIcon, LanguageIcon, LoginIcon, LogoutIcon, SettingsIcon } from 'src/shared/icons/Icons'
import { useNavigateToLogin } from 'src/shared/utils/hooks/navigation-hooks'
import { SessionTimeoutContext } from 'src/user/SessionTimeoutContext'
import { UserContext } from 'src/user/UserContext'
import styled from 'styled-components'
import { S } from '../styled/S'

export const APPLICATION_TOOLBAR_HEIGHT = '65px'

const LogoWrapperStyled = styled('div')`
  flex-grow: 1;
  height: ${APPLICATION_TOOLBAR_HEIGHT};

  &,
  a {
    display: flex;
    align-items: center;
  }

  svg {
    height: 60px;
    width: 200px;
  }
`

const AccountMenu = styled(Menu)`
  & .MuiListItemText-inset {
    padding-left: ${({ theme }) => theme.spacing(5)};
  }
`

const ListItemIconStyled = styled(ListItemIcon)`
  &.MuiListItemIcon-root {
    min-width: ${({ theme }) => theme.spacing(5)};
  }
`

const TabStyled = styled(TabNavLink)`
  height: ${APPLICATION_TOOLBAR_HEIGHT};
  padding-left: 0;
  padding-right: 0;
  letter-spacing: ${({ theme }) => theme.typography.button.letterSpacing};

  &:hover {
    background-color: transparent;
  }

  &.MuiTab-labelIcon {
    min-height: auto;
  }

  & .MuiSvgIcon-root {
    margin-left: ${({ theme }) => theme.spacing(0.5)};
  }

  &.MuiButtonBase-root {
    line-height: 100%;
  }
`

const AvatarStyled = styled(Avatar)`
  background-color: ${({ theme }) => theme.colors.primary.main};
  ${TYPOGRAPHY_MIXIN.button};
`

const DividerWithFilteredProps = (props: DividerProps) => {
  const { orientation, variant, flexItem, sx, key } = props
  return <Divider orientation={orientation} variant={variant} flexItem={flexItem} sx={sx} key={key} />
}

export const ApplicationToolbar = (): ReactElement => {
  const { manualLogout } = useContext(SessionTimeoutContext)
  const login = useNavigateToLogin()

  const { user } = useContext(UserContext)
  const globalRoles = user.roles
  const { environment, featureBranchName } = useEnvironment()
  const { pathname } = useLocation()
  const { getMessage } = useMessageSource()

  const [anchorElAccountMenu, setAnchorElAccountMenu] = useState<null | HTMLElement>(null)
  const [selectedLocale, setSelectedLocale] = useState<SupportedLocale>(user.language)
  const [searchParams, setSearchParams] = useSearchParams()

  const openAccountMenu = Boolean(anchorElAccountMenu)

  const canViewAdministration = PermissionService.permissionToViewAdministrationLink(globalRoles)
  const { canViewReporting } = PermissionService.permissionToViewNavLinkForReporting(globalRoles)
  const { canView: canViewSuccessFactors } = usePermissionsForSuccessFactors()

  const {
    changeLocale,
    user: { firstName, lastName, anonymous },
  } = useContext(UserContext)

  const successFactorsPublicSpace = anonymous && pathname.startsWith(BASE_URL.PUBLIC_SUCCESS_FACTOR_TOOL)

  useEffect(() => {
    // assert the lang search param as SupportedLocale
    const locale = searchParams.get('lang') as SupportedLocale

    // explicitly check whether lang param is one of the supported locales
    const supportedLocales: SupportedLocale[] = ['en', 'de', 'it', 'fr']
    if (locale && locale !== user.language && supportedLocales.some((lang) => lang === locale)) {
      changeLocale(locale)
      setSelectedLocale(locale)
    }
  }, [changeLocale, searchParams, user.language])

  const handleAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElAccountMenu(event.currentTarget)
  }

  let selectedNavigationTabValue:
    | '/'
    | '/kap'
    | '/pf-pgv'
    | '/pf-kap'
    | '/factsheets'
    | '/reporting'
    | '/success-factors'
    | false

  if (pathname.startsWith('/kap')) {
    selectedNavigationTabValue = '/kap'
  } else if (pathname.startsWith('/pf-pgv')) {
    selectedNavigationTabValue = '/pf-pgv'
  } else if (pathname.startsWith('/pf-kap')) {
    selectedNavigationTabValue = '/pf-kap'
  } else if (pathname.startsWith('/factsheets')) {
    selectedNavigationTabValue = '/factsheets'
  } else if (pathname.startsWith('/reporting')) {
    selectedNavigationTabValue = '/reporting'
  } else if (pathname.startsWith('/success-factors')) {
    selectedNavigationTabValue = '/success-factors'
  } else if (pathname.startsWith('/administration')) {
    selectedNavigationTabValue = false
  } else if (pathname === '/') {
    selectedNavigationTabValue = '/'
  } else {
    // Set to false to intentionally deselect all tabs in the navigation and eliminate error messages in console
    selectedNavigationTabValue = false
  }

  const handleCloseAccountMenu = () => {
    setAnchorElAccountMenu(null)
  }

  const handleChangeLocale = (locale: SupportedLocale) => () => {
    const landingPage = `/${ROUTES.PublicLandingPage.path}`
    const sftLandingPage = `/${ROUTES.SuccessFactorsPublicLanding.path}`

    handleCloseAccountMenu()
    if (pathname === landingPage || pathname === sftLandingPage) {
      if (searchParams.has('lang')) {
        setSearchParams({ lang: locale })
      }
      if (searchParams.has('email') && searchParams.has('lang')) {
        setSearchParams({ email: searchParams.get('email') as string, lang: locale })
      }
    }
    changeLocale(locale)
    setSelectedLocale(locale)
  }

  const handleLogout = () => {
    handleCloseAccountMenu()
    manualLogout()
  }

  const renderTabs = () => {
    const tabs: React.ReactNode[] = []

    tabs.push(<TabStyled key="task-list" label={getMessage('label.navigation.task_list')} value="/" to={`/`} />)

    if (PermissionService.permissionToViewNavLink(PROJECT.KAP, globalRoles)) {
      tabs.push(
        <TabStyled key="kap" label={getMessage('label.navigation.kap')} value="/kap" to={`/${ROUTES.KapIndex.path}`} />,
      )
    }

    if (PermissionService.permissionToViewNavLink(PROJECT.PF_KAP, globalRoles)) {
      tabs.push(
        <TabStyled
          key="pf-kap"
          label={getMessage('label.navigation.pf_kap')}
          value="/pf-kap"
          to={`/${ROUTES.PfKapIndex.path}`}
        />,
      )
    }

    if (PermissionService.permissionToViewNavLink(PROJECT.PF_PGV, globalRoles)) {
      tabs.push(
        <TabStyled
          key="pf-pgv"
          label={getMessage('label.navigation.pf_pgv')}
          value="/pf-pgv"
          to={`/${ROUTES.PfPgvIndex.path}`}
        />,
      )
    }

    if (canViewSuccessFactors) {
      tabs.push(
        <TabStyled
          key={'success-factors'}
          label={getMessage('label.navigation.success.factors')}
          value="/success-factors"
          to={`/${ROUTES.SuccessFactorsIndex.path}`}
        />,
      )
    }

    if (PermissionService.permissionToViewNavLink(PROCESS.FACTSHEET, globalRoles)) {
      tabs.push(
        <TabStyled
          key={'factsheets'}
          label={getMessage('label.navigation.factsheets')}
          value="/factsheets"
          to={`/${ROUTES.FactsheetsIndex.path}`}
        />,
      )
    }

    if (canViewReporting) {
      tabs.push(
        <TabStyled
          key={'reporting'}
          label={getMessage('label.navigation.reports')}
          value="/reporting"
          to={`/${ROUTES.ReportingIndex.path}`}
        />,
      )
    }

    const tabsWithDividers: React.ReactNode[] = []

    tabs.forEach((tab, index) => {
      tabsWithDividers.push(tab)
      if (index < tabs.length - 1) {
        tabsWithDividers.push(
          <DividerWithFilteredProps
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ my: 3, mx: 2 }}
            key={index}
          />,
        )
      }
    })

    return tabsWithDividers
  }

  return (
    <Toolbar>
      {/* Logo */}
      <LogoWrapperStyled>
        <Routes>
          <Route
            path="/*"
            element={
              <Link to="/">
                <Logo />
              </Link>
            }
          />
          <Route path="/pub/*" element={<Logo />} />
        </Routes>
        {environment === ENVIRONMENT.FEATURE &&
          featureBranchName !== '' &&
          featureBranchName !== undefined &&
          featureBranchName !== null && (
            <Typography variant="caption" color="textSecondary" sx={{ marginLeft: 5, marginRight: 5 }}>
              {`Feature Branch: ${featureBranchName}`}
            </Typography>
          )}
      </LogoWrapperStyled>

      {/* Tabs */}
      {!anonymous && (
        <>
          <Tabs sx={{ marginRight: 3 }} value={selectedNavigationTabValue} variant="scrollable" textColor="inherit">
            {/* Hidden placeholder tab */}
            <TabStyled value="/" label="" to="/" sx={{ visibility: 'hidden' }} />

            {renderTabs()}
          </Tabs>

          <IconButton onClick={handleAccountMenu} size="small">
            <AvatarStyled>{`${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`}</AvatarStyled>
          </IconButton>
        </>
      )}

      {/* Account menu */}
      {anonymous && (
        <IconButton onClick={handleAccountMenu} size="small">
          {pathname === BASE_URL.PUBLIC_LANDING ? (
            <LanguageIcon />
          ) : (
            <AvatarStyled>
              <AccountIcon />
            </AvatarStyled>
          )}
        </IconButton>
      )}
      <AccountMenu
        anchorEl={anchorElAccountMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        keepMounted
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={openAccountMenu}
        MenuListProps={{
          variant: 'menu',
        }}
        onClose={handleCloseAccountMenu}
        elevation={4}
        PaperProps={{
          sx: {
            minWidth: anonymous ? '10rem' : '20rem',
          },
        }}
      >
        {!anonymous && (
          <MenuItem
            sx={{
              paddingLeft: 1.5,
              '&:hover, &:focus-visible': { backgroundColor: 'transparent', cursor: 'default' },
            }}
          >
            <AvatarStyled
              sx={{
                marginRight: 1.5,
                height: '2rem',
                width: '2rem',
              }}
            >{`${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`}</AvatarStyled>
            <S.DataGrid.TwoLineCell>
              <Typography>{`${firstName} ${lastName}`}</Typography>
              <Typography variant="caption" color="textSecondary">
                {user.email}
              </Typography>
            </S.DataGrid.TwoLineCell>
          </MenuItem>
        )}
        {!anonymous && <Divider />}
        {!anonymous && canViewAdministration && (
          <MenuItemNavLink
            to={ROUTES.RoundManagementIndex.path}
            icon={<SettingsIcon sx={{ mr: 2 }} />}
            primary={getMessage('label.administration')}
            endComponent={
              <ListItemIcon sx={{ color: 'inherit', paddingLeft: 1 }}>
                <Tooltip title={getMessage('label.navigation.gfch.only.tooltip')} placement="right-end">
                  <Lock color="inherit" fontSize="inherit" />
                </Tooltip>
              </ListItemIcon>
            }
            onClick={handleCloseAccountMenu}
          />
        )}
        {!anonymous && canViewAdministration && <Divider />}
        {successFactorsPublicSpace && (
          <MenuItem onClick={login}>
            <ListItemIconStyled>
              <LoginIcon />
            </ListItemIconStyled>
            <ListItemText>{getMessage('button.login')}</ListItemText>
          </MenuItem>
        )}
        {successFactorsPublicSpace && <Divider />}
        <MenuItem onClick={handleChangeLocale('de')}>
          {selectedLocale === 'de' && (
            <ListItemIconStyled>
              <CheckIcon />
            </ListItemIconStyled>
          )}
          <ListItemText inset={!(selectedLocale === 'de')}>Deutsch</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleChangeLocale('fr')}>
          {selectedLocale === 'fr' && (
            <ListItemIconStyled>
              <CheckIcon />
            </ListItemIconStyled>
          )}
          <ListItemText inset={!(selectedLocale === 'fr')}>Français</ListItemText>
        </MenuItem>
        {!successFactorsPublicSpace && (
          <MenuItem onClick={handleChangeLocale('it')}>
            {selectedLocale === 'it' && (
              <ListItemIconStyled>
                <CheckIcon />
              </ListItemIconStyled>
            )}
            <ListItemText inset={!(selectedLocale === 'it')}>Italiano</ListItemText>
          </MenuItem>
        )}
        {environment === ENVIRONMENT.DEV && (
          <MenuItem onClick={handleChangeLocale('en')}>
            {selectedLocale === 'en' && (
              <ListItemIconStyled>
                <CheckIcon />
              </ListItemIconStyled>
            )}
            <ListItemText inset={!(selectedLocale === 'en')}>English</ListItemText>
          </MenuItem>
        )}
        {!anonymous && <Divider />}
        {!anonymous && (
          <MenuItem onClick={handleLogout}>
            <ListItemIconStyled>
              <LogoutIcon />
            </ListItemIconStyled>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        )}
      </AccountMenu>
    </Toolbar>
  )
}
